import { alpha, createTheme } from "@mui/material/styles";

export const colorPresets = {
  slate: {
    primary: "#2F5D8C",
    primaryDark: "#1F4367",
    secondary: "#4F7C82",
    accent: "#E8A96B",
    background: "#F4F7FB",
    surface: "#FFFFFF",
    surfaceMuted: "#ECF2F8",
    textPrimary: "#1F2937",
    textSecondary: "#5F6C7B",
    border: "#D8E1EA",
    success: "#2E7D5B",
    warning: "#C67A2C",
    error: "#C45353",
    info: "#3A78C2",
  },
  evergreen: {
    primary: "#2F6D5B",
    primaryDark: "#215041",
    secondary: "#6B8790",
    accent: "#D9A15F",
    background: "#F3F7F5",
    surface: "#FFFFFF",
    surfaceMuted: "#EAF2EE",
    textPrimary: "#1D2A24",
    textSecondary: "#5F6F68",
    border: "#D7E2DC",
    success: "#2C7A5B",
    warning: "#B97A34",
    error: "#C45353",
    info: "#3A78C2",
  },
  dusk: {
    primary: "#5A5FCF",
    primaryDark: "#4347A0",
    secondary: "#6C7A89",
    accent: "#DE9A70",
    background: "#F6F6FC",
    surface: "#FFFFFF",
    surfaceMuted: "#EEECFA",
    textPrimary: "#24283A",
    textSecondary: "#62677B",
    border: "#D9DCEF",
    success: "#397D6A",
    warning: "#C17F35",
    error: "#C45353",
    info: "#4873D4",
  },
};

export const DEFAULT_COLOR_PRESET = "slate";

export function createAppTheme(presetName = DEFAULT_COLOR_PRESET) {
  const preset = colorPresets[presetName] || colorPresets[DEFAULT_COLOR_PRESET];

  return createTheme({
    palette: {
      mode: "light",
      primary: {
        main: preset.primary,
        dark: preset.primaryDark,
        contrastText: "#FFFFFF",
      },
      secondary: {
        main: preset.secondary,
        contrastText: "#FFFFFF",
      },
      success: { main: preset.success },
      warning: { main: preset.warning },
      error: { main: preset.error },
      info: { main: preset.info },
      background: {
        default: preset.background,
        paper: preset.surface,
      },
      text: {
        primary: preset.textPrimary,
        secondary: preset.textSecondary,
      },
      divider: preset.border,
      enterprise: {
        accent: preset.accent,
        surfaceMuted: preset.surfaceMuted,
        borderStrong: alpha(preset.primaryDark, 0.16),
      },
    },
    shape: {
      borderRadius: 14,
    },
    spacing: 8,
    typography: {
      fontFamily: [
        "Roboto",
        "\"Noto Sans JP\"",
        "\"Hiragino Kaku Gothic ProN\"",
        "\"Yu Gothic UI\"",
        "sans-serif",
      ].join(","),
      h3: {
        fontWeight: 700,
        letterSpacing: "-0.02em",
      },
      h4: {
        fontWeight: 700,
        letterSpacing: "-0.02em",
      },
      h5: {
        fontWeight: 700,
      },
      h6: {
        fontWeight: 700,
      },
      subtitle1: {
        fontWeight: 600,
      },
      body1: {
        lineHeight: 1.7,
      },
      body2: {
        lineHeight: 1.6,
      },
      button: {
        fontWeight: 700,
        letterSpacing: "0.01em",
        textTransform: "none",
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            height: "100%",
          },
          body: {
            minHeight: "100%",
            backgroundColor: preset.background,
            backgroundImage: `linear-gradient(180deg, ${alpha(
              preset.surfaceMuted,
              0.9
            )} 0%, ${preset.background} 280px)`,
          },
          "#root": {
            minHeight: "100vh",
          },
          a: {
            color: "inherit",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            border: `1px solid ${alpha(preset.primaryDark, 0.08)}`,
            backgroundImage: "none",
          },
          rounded: {
            borderRadius: 18,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            border: `1px solid ${alpha(preset.primaryDark, 0.08)}`,
            boxShadow: `0 18px 48px ${alpha(preset.primaryDark, 0.08)}`,
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            minHeight: 44,
            borderRadius: 12,
            paddingInline: 18,
          },
          containedPrimary: {
            boxShadow: `0 10px 24px ${alpha(preset.primary, 0.18)}`,
            "&:hover": {
              boxShadow: `0 14px 28px ${alpha(preset.primary, 0.24)}`,
            },
          },
          outlined: {
            borderColor: alpha(preset.primaryDark, 0.18),
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            borderBottom: `1px solid ${alpha(preset.primaryDark, 0.12)}`,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: `1px solid ${alpha(preset.primaryDark, 0.1)}`,
            backgroundColor: alpha(preset.surface, 0.98),
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          size: "small",
          fullWidth: true,
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            backgroundColor: alpha(preset.surface, 0.92),
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottomColor: preset.border,
          },
          head: {
            fontWeight: 700,
            color: preset.textSecondary,
            backgroundColor: alpha(preset.surfaceMuted, 0.85),
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 14,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 20,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            marginBottom: 4,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 999,
          },
        },
      },
    },
  });
}

const appTheme = createAppTheme();

export default appTheme;
